import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, TrendingUp, Users, Mail, MessageSquare, CheckCircle2, XCircle } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, LineChart, Line, CartesianGrid } from 'recharts';
import { toast } from 'sonner';
import api from '@/utils/api';

const SOURCE_LABELS = {
  contact_form: 'Contact Form',
  buy_form: 'Buyer Inquiry',
  sell_form: 'Seller Inquiry',
  tour_request: 'Tour Request',
  booking: 'Booking Modal',
  property_gate: 'Property Gate',
  home_valuation: 'Home Valuation',
};

const SEQUENCE_LABELS = {
  buyer_nurture: 'Buyer Drip',
  seller_nurture: 'Seller Drip',
  general_nurture: 'General Drip',
};

const StatCard = ({ label, value, subtext, icon: Icon, accent = '#BCA480' }) => (
  <Card data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
          <div className="text-3xl font-bold text-[#434345] mt-1">{value}</div>
          {subtext && <div className="text-xs text-gray-500 mt-1">{subtext}</div>}
        </div>
        <div className="p-2 rounded-lg" style={{ backgroundColor: `${accent}20` }}>
          <Icon className="h-5 w-5" style={{ color: accent }} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const AnalyticsDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/admin/analytics/summary');
        setData(res.data);
      } catch (e) {
        toast.error('Failed to load analytics');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#faf8f4]">
        <div className="text-gray-500">Loading analytics…</div>
      </div>
    );
  }
  if (!data) return null;

  const t = data.totals;
  const sources30d = Object.entries(data.lead_sources_30d).map(([k, v]) => ({
    source: SOURCE_LABELS[k] || k, count: v,
  }));
  const bySequence = Object.entries(data.nurture.by_sequence).map(([k, v]) => ({
    sequence: SEQUENCE_LABELS[k] || k,
    active: v.active,
    completed: v.completed,
    unsubscribed: v.unsubscribed,
  }));

  return (
    <div className="min-h-screen bg-[#faf8f4] py-8 px-4 sm:px-6 lg:px-8" data-testid="analytics-dashboard">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <Button asChild variant="ghost" size="sm" data-testid="back-to-admin">
            <Link to="/admin">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back to Admin
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-[#434345]">Analytics</h1>
            <p className="text-sm text-gray-500">
              Live lead, nurture, and SMS metrics · Refreshed {new Date(data.generated_at).toLocaleTimeString()}
            </p>
          </div>
        </div>

        {/* Top-level KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard label="Total Leads" value={t.leads_all_time} subtext={`${t.leads_30d} in last 30d`} icon={Users} />
          <StatCard label="Leads · 7d" value={t.leads_7d} subtext="Recent momentum" icon={TrendingUp} accent="#8B9DC3" />
          <StatCard label="Nurture Enrolled" value={t.nurture_enrollments} subtext={`${t.nurture_emails_sent} emails sent`} icon={Mail} accent="#A5C8A0" />
          <StatCard label="SMS Opt-in Rate" value={`${t.sms_opt_in_rate_pct}%`} subtext={`${t.sms_consents} explicit consents`} icon={MessageSquare} accent="#D4A574" />
        </div>

        {/* Trend chart */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-lg">Leads Over Time (Last 30 Days)</CardTitle>
          </CardHeader>
          <CardContent>
            <div style={{ width: '100%', height: 260 }}>
              <ResponsiveContainer>
                <LineChart data={data.daily_leads_30d} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} interval={2} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="leads" stroke="#BCA480" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Two column: sources + nurture perf */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Leads by Source (30d)</CardTitle>
            </CardHeader>
            <CardContent>
              <div style={{ width: '100%', height: 260 }}>
                <ResponsiveContainer>
                  <BarChart data={sources30d} layout="vertical" margin={{ top: 5, right: 20, left: 60, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                    <YAxis type="category" dataKey="source" tick={{ fontSize: 11 }} width={100} />
                    <Tooltip contentStyle={{ fontSize: 12 }} />
                    <Bar dataKey="count" fill="#BCA480" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Nurture Drip Performance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3" data-testid="nurture-breakdown">
                {bySequence.map(seq => (
                  <div key={seq.sequence} className="p-3 border border-gray-100 rounded-lg">
                    <div className="flex justify-between items-baseline mb-2">
                      <span className="font-semibold text-sm text-[#434345]">{seq.sequence}</span>
                      <span className="text-xs text-gray-500">
                        {seq.active + seq.completed + seq.unsubscribed} enrolled
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center text-xs">
                      <div className="bg-blue-50 rounded p-2">
                        <div className="font-bold text-blue-700">{seq.active}</div>
                        <div className="text-blue-500">Active</div>
                      </div>
                      <div className="bg-green-50 rounded p-2">
                        <div className="font-bold text-green-700">{seq.completed}</div>
                        <div className="text-green-500">Completed</div>
                      </div>
                      <div className="bg-red-50 rounded p-2">
                        <div className="font-bold text-red-700">{seq.unsubscribed}</div>
                        <div className="text-red-500">Unsubscribed</div>
                      </div>
                    </div>
                  </div>
                ))}
                <div className="text-xs text-gray-500 pt-2 border-t">
                  Overall unsubscribe rate: <strong>{data.nurture.unsubscribe_rate_pct}%</strong>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* SMS + Transactions summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">SMS Compliance & Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600 flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-600" />Explicit opt-ins</span>
                  <span className="font-semibold">{data.sms.consents}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 flex items-center gap-2"><XCircle className="h-4 w-4 text-orange-500" />Opt-outs via STOP</span>
                  <span className="font-semibold">{data.sms.opt_outs_stop}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 flex items-center gap-2"><XCircle className="h-4 w-4 text-gray-400" />Declined on form</span>
                  <span className="font-semibold">{data.sms.opt_outs_form}</span>
                </div>
                <div className="flex justify-between pt-2 border-t">
                  <span className="text-gray-600 flex items-center gap-2"><MessageSquare className="h-4 w-4 text-[#BCA480]" />Inbound messages received</span>
                  <span className="font-semibold">{data.sms.inbound_total}</span>
                </div>
                <div className="flex justify-between font-semibold pt-2">
                  <span>Opt-in rate</span>
                  <span className="text-[#BCA480]">{data.sms.opt_in_rate_pct}%</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Transactions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total transactions</span>
                  <span className="font-semibold">{t.transactions_total}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Currently active</span>
                  <span className="font-semibold">{t.transactions_active}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Closed / cancelled</span>
                  <span className="font-semibold">{t.transactions_total - t.transactions_active}</span>
                </div>
                <div className="pt-3 mt-2 border-t">
                  <Button asChild variant="outline" size="sm" className="w-full">
                    <Link to="/admin">Manage in Admin →</Link>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
